import {
  Download,
  ListChecks,
  MailPlus,
  Share2,
  Upload,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Reveal } from "@/shared/components/motion/Reveal";

const FEATURES: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Users,
    title: "Contacts & households",
    body: "Keep each person on hand, then gather couples, families, and roommates under one shared mailing address. One invitation, one address, every name beautifully accounted for.",
  },
  {
    icon: Share2,
    title: "Collect contacts",
    body: "Share a link and let guests add their own details. Each submission waits in a review queue — never straight onto your list — with gentle duplicate detection that offers to merge rather than clutter.",
  },
  {
    icon: MailPlus,
    title: "Request an address",
    body: "Already have someone's email or phone? Send a thoughtfully pre-filled request, one at a time or all at once, and let them confirm their mailing address in a moment.",
  },
  {
    icon: ListChecks,
    title: "Lists",
    body: "Shape your contacts into a wedding list, a holiday-card list, a gathering of any kind. A single contact can belong to many lists, and your chosen view is always a click away.",
  },
  {
    icon: Upload,
    title: "Import from anywhere",
    body: "Bring in a CSV or Excel file from Google Sheets, a past planner, or an old export. Match columns to fields, preview before you commit, and let forgiving matching tidy up messy country and state values.",
  },
  {
    icon: Download,
    title: "Export your way",
    body: 'Choose, rename, and reorder fields; collapse each household to a single line; and save your "print shop" and "planner" layouts so the right format is always ready.',
  },
];

/**
 * Feature list. On desktop the heading is large and sticky on the left while the
 * cards reveal one-by-one on the right.
 */
export function FeaturesSection() {
  return (
    <Section spacing="marketing" id="features">
      <Container size="5xl">
        <div className="grid items-start gap-12 lg:grid-cols-[1fr_3fr] lg:gap-16">
          <div className="flex flex-col gap-4 lg:sticky lg:top-32">
            <p className="type-label-sm tracking-wider uppercase text-text-muted">
              Features
            </p>
            <h2 className="type-display-cta text-text">
              Everything your guest list needs, in one place.
            </h2>
          </div>

          <div className="flex flex-col gap-6">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <Reveal
                key={title}
                className="flex flex-col gap-4 rounded-xl glass-surface p-8 shadow-md"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full glass-surface-subtle text-brand">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <h3 className="type-heading-xs text-text">{title}</h3>
                <p className="type-body-base text-text-muted">{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}
